import { PolicyGetResponse, PolicyTransactionType } from '@lio/api-types';

import {
  PolicyTransactionTypes,
  EndorsementReason,
  PolicyTransactionStatus,
  PolicyCancelationMethod,
  PolicyCancelationReason,
  PolicyCancelationSource,
  PolicyReinstatementReason,
} from '@models/policyTransaction';
import { setDateToMidnight } from '@utils/date';
import { companyOptions } from '../PolicyTransactionDialog.constants';

export const getCreatePolicyTransactionRequestFromFormData = ({
  transactionType,
  policy,
  formData,
  renewalQuoteId,
}: {
  transactionType: PolicyTransactionType | 'Rollback';
  policy: PolicyGetResponse;
  formData: { [fieldName: string]: any };
  renewalQuoteId?: string;
}) => {
  switch (transactionType) {
    case PolicyTransactionTypes.Renewal:
      return {
        status: PolicyTransactionStatus.Created,
        transactionType: PolicyTransactionTypes.Renewal,
        policyId: policy?.id || '',
        effectiveOn: setDateToMidnight(formData?.effectiveDate),
      };

    case PolicyTransactionTypes.Endorsement:
      return {
        status: PolicyTransactionStatus.Created,
        description: formData.description,
        transactionType: PolicyTransactionTypes.Endorsement,
        effectiveOn: setDateToMidnight(formData.effectiveOn),
        policyId: policy?.id || '',
        policyEndorsementTransaction: {
          reason: formData.reason as EndorsementReason,
        },
      };

    case PolicyTransactionTypes.BillingPlanEndorsement:
      return {
        status: PolicyTransactionStatus.Processed,
        transactionType: PolicyTransactionTypes.BillingPlanEndorsement,
        quoteId: policy.boundQuote?.id ?? '',
        policyId: policy?.id || '',
        effectiveOn: setDateToMidnight(formData.effectiveOn),
        billingPlanId: formData.policyBillPlan,
      };

    case PolicyTransactionTypes.Rewrite:
      return {
        description: formData.description,
        effectiveOn: formData.effectiveOn ? setDateToMidnight(formData.effectiveOn) : setDateToMidnight(new Date()),
        transactionType: PolicyTransactionTypes.Rewrite,
        status: PolicyTransactionStatus.Created,
        policyId: policy?.id || '',
        policyRewriteTransaction: {
          description: 'Rewrite',
          useSamePolicyNumber: formData.retainPolicyNumber === true,
        },
      };

    case PolicyTransactionTypes.Cancelation: {
      const reason = formData.reason as PolicyCancelationReason;
      const includesCompanyReasons = companyOptions.map((cr) => cr.value).includes(reason ?? '');
      let source: PolicyCancelationSource | string;

      if (reason === PolicyCancelationReason.Other) {
        source = formData.source as PolicyCancelationSource;
      } else if (includesCompanyReasons) {
        source = PolicyCancelationSource.Company;
      } else {
        source = PolicyCancelationSource.Insured;
      }

      const blockCustomer = formData.blockCustomer === 'true';

      return {
        status: PolicyTransactionStatus.Processed,
        description: formData.notes,
        transactionType: PolicyTransactionTypes.Cancelation,
        effectiveOn: setDateToMidnight(formData.effectiveDate as Date),
        policyId: policy?.id || '',
        policyCancelationTransaction: {
          source: source as PolicyCancelationSource,
          reason: reason as PolicyCancelationReason,
          method: formData.method as PolicyCancelationMethod,
          grounds: reason === PolicyCancelationReason.CompanyRequest ? formData.grounds : undefined,
          certifiedMailRequired: formData.certifiedMailRequired === 'true',
          underwriterJustification: formData.underwriterJustification,
        },
        ...(blockCustomer && { customer: { isBlocked: true, blockedByReferencePolicyId: policy?.id } }),
      };
    }
    case PolicyTransactionTypes.Reinstatement: {
      return {
        status: PolicyTransactionStatus.Processed,
        transactionType: PolicyTransactionTypes.Reinstatement,
        description: formData.description,
        effectiveOn: setDateToMidnight(formData.effectiveDate as Date),
        policyId: policy?.id || '',
        policyReinstatementTransaction: {
          reason: formData.reason as PolicyReinstatementReason,
          claimsLossesPostCancelation: formData.claimsLossesPostCancelation === 'true',
          certifiedMailRequired: formData.certifiedMailRequired === 'true',
        },
      };
    }

    case PolicyTransactionTypes.DoNotRenew: {
      const shouldBlockCustomer = formData.blockCustomer === 'true';
      return {
        transactionType: PolicyTransactionTypes.DoNotRenew,
        policyId: policy?.id,
        effectiveOn: setDateToMidnight(new Date()),
        status: PolicyTransactionStatus.Processed,
        policyNonrenewalTransaction: {
          underwriterJustification: formData.underwriterJustification,
          grounds: formData.grounds,
        },
        ...(shouldBlockCustomer && {
          customer: { isBlocked: true },
        }),
      };
    }

    case PolicyTransactionTypes.RescindDoNotRenew: {
      const isCustomerBlocked = Boolean((policy as any)?.customer?.isBlocked);
      return {
        transactionType: PolicyTransactionTypes.RescindDoNotRenew,
        policyId: policy?.id,
        effectiveOn: setDateToMidnight(new Date()),
        status: PolicyTransactionStatus.Processed,
        ...(isCustomerBlocked && {
          customer: { isBlocked: false },
        }),
      };
    }

    case PolicyTransactionTypes.ConditionalRenewal: {
      return {
        transactionType: PolicyTransactionTypes.ConditionalRenewal,
        policyId: policy?.id,
        effectiveOn: setDateToMidnight(new Date()),
        status: PolicyTransactionStatus.Processed,
        policyConditionalRenewalTransaction: {
          underwriterJustification: formData.underwriterJustification,
          grounds1: formData.grounds1,
          grounds2: formData.grounds2 || undefined,
          grounds3: formData.grounds3 || undefined,
          changeInCoverageDetails: formData.changeInCoverageDetails,
          renewalQuoteId: renewalQuoteId ?? '',
        },
      };
    }

    case PolicyTransactionTypes.ManageRoles: {
      return {
        transactionType: PolicyTransactionTypes.ManageRoles,
        policyId: policy?.id,
        effectiveOn: setDateToMidnight(new Date()),
        status: PolicyTransactionStatus.Processed,
        description: formData.description,
        reason: formData.reason,
        policyBrokerReassignmentTransaction: {
          newBrokerageId: formData.brokerage,
          newBrokerageProducerId: formData.producer,
        },
      };
    }

    default:
      throw Error('Transaction Type Not Implemented');
  }
};
